import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getProducts } from '../firebase/db';
import {
  getLocalizedProductName,
  getLocalizedCategory,
  getLocalizedCapacity,
  getLocalizedShortDesc,
  translateProductText,
  normalizeLang
} from '../utils/productLocalization';
import './Products.css';

const Products = () => {
  const { t, i18n } = useTranslation();
  const currentLang = normalizeLang(i18n.language || 'mr');
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [loading, setLoading] = useState(true);
  const [, setDynamicTrigger] = useState(0);

  const getCategoryLabel = (cat, prod) => getLocalizedCategory(cat, t, currentLang, prod);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      const data = await getProducts();
      setProducts(data);
      setFilteredProducts(data);

      // Extract unique categories
      const cats = ['All', ...new Set(data.map(item => item.category).filter(Boolean))];
      setCategories(cats);
      setLoading(false);
    };
    fetchProducts();
  }, []);

  // Background on-demand dynamic translation for any products lacking translation
  useEffect(() => {
    if (currentLang === 'en' || !products || products.length === 0) return;

    let isMounted = true;
    products.forEach(async (p) => {
      const nameField = currentLang === 'mr' ? (p.name_mr || p.nameMr) : (p.name_kn || p.nameKn);
      const descField = currentLang === 'mr' ? (p.shortDescription_mr || p.shortDescriptionMr) : (p.shortDescription_kn || p.shortDescriptionKn);

      let updated = false;
      if (!nameField && p.name) {
        await translateProductText(p.name, currentLang);
        updated = true;
      }
      if (!descField && (p.shortDescription || p.description)) {
        await translateProductText(p.shortDescription || p.description, currentLang);
        updated = true;
      }
      if (updated && isMounted) {
        setDynamicTrigger(prev => prev + 1);
      }
    });

    return () => { isMounted = false; };
  }, [products, currentLang]);

  const filterProducts = (category) => {
    setActiveCategory(category);
    if (category === 'All') {
      setFilteredProducts(products);
    } else {
      setFilteredProducts(products.filter(p => p.category === category));
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <div className="products-page">
      <div className="page-header">
        <div className="banner-particle bp-1" />
        <div className="banner-particle bp-2" />
        <div className="banner-particle bp-3" />
        <div className="container">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="banner-badge">{t('productsBadge')}</span>
            <h1>{t('productsTitle1')} <span className="highlight-red">{t('productsTitle2')}</span></h1>
          </motion.div>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}>
            {t('productsSubtitle')}
          </motion.p>
        </div>
      </div>

      <section className="section bg-light">
        <div className="container">
          {/* Category Filter */}
          <div className="category-filter">
            {categories.map((cat, index) => (
              <button
                key={index}
                className={`filter-btn ${activeCategory === cat ? 'active' : ''}`}
                onClick={() => filterProducts(cat)}
              >
                {getCategoryLabel(cat)}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
            </div>
          ) : (
            <motion.div
              className="products-grid"
              initial="hidden"
              animate="visible"
              variants={{
                visible: { transition: { staggerChildren: 0.1 } }
              }}
            >
              {filteredProducts.length > 0 ? (
                filteredProducts.map((product) => (
                  <motion.div key={product.id} variants={fadeInUp} className="product-card glass">
                    <div className="product-img">
                      <img src={product.images && product.images[0] ? product.images[0] : 'https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=800'} alt={getLocalizedProductName(product, currentLang)} />
                      <div className="product-category">{getCategoryLabel(product.category, product)}</div>
                    </div>
                    <div className="product-info">
                      <h3>{getLocalizedProductName(product, currentLang)}</h3>
                      <p className="capacity">
                        {getLocalizedCapacity(product.capacity, currentLang, product)}
                      </p>
                      <p className="desc">{getLocalizedShortDesc(product, t, currentLang)}</p>
                      <Link to={`/product/${product.id}`} className="btn btn-outline btn-sm mt-auto">
                        {t('btnViewDetail')}
                      </Link>
                    </div>
                  </motion.div>
                ))
              ) : (
                <div className="no-products">
                  <h3>{t('noProductsFound')}</h3>
                </div>
              )}
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Products;
